import * as React from "react";
import { Box, Button, Typography, Stack } from "@mui/material";
import { useI18n } from "../i18n/I18nProvider";


type Props = {
  handlePageChange: (page: string) => void;
};

export default function WelcomeScreen({ handlePageChange }: Props) {
  const { lang } = useI18n();
  const isDE = lang === "de";
  const cards = isDE
    ? [
        ["Bewusst", "Verstehe deine Rolle, Bedrohungen und Verfügbarkeit."],
        ["Bereit", "Durchlaufe praktische Readiness-Stationen."],
        ["Gemeinsam", "Verbinde Erkenntnisse mit Familie und Unternehmen."],
      ]
    : [
        ["Aware", "Understand your role, threats and availability."],
        ["Prepare", "Complete practical readiness stations."],
        ["Share", "Connect the insight to family and company."],
      ];

  return (
    <Box
      onClick={() => handlePageChange("Entry")}
      sx={{
        position: "relative",
        minHeight: "100vh",
        cursor: "pointer",
        backgroundImage: "url('/background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, sm: 4 },
        py: { xs: 6, sm: 8 },
      }}
    >
      {/* DARK OVERLAY */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.85))",
        }}
      />

      {/* CONTENT */}
      <Stack
        spacing={4}
        sx={{
          position: "relative",
          textAlign: "center",
          px: 3,
          maxWidth: 980,
          width: "100%",
        }}
      >
        <Typography
          sx={{
            color: "white",
            fontSize: { xs: "2rem", md: "3rem" },
            fontWeight: 950,
            textShadow: "0 0 20px rgba(255,255,255,0.15)",
            lineHeight: 1.1,
          }}
        >
          {isDE
            ? "Willkommen im takeKONTROL Lab"
            : "Welcome to the takeKONTROL Lab"}
        </Typography>

        <Typography
          sx={{
            color: "rgba(255,255,255,0.8)",
            fontSize: { xs: "1.05rem", md: "1.25rem" },
            fontWeight: 700,
            maxWidth: 760,
            mx: "auto",
            lineHeight: 1.6,
          }}
        >
          {isDE
            ? "Baue deinen individuellen Lab Score in einer geführten Readiness Journey auf."
            : "Build your individual Lab Score through a guided readiness journey."}
        </Typography>

        <Typography
          sx={{
            color: "rgba(255,255,255,0.74)",
            maxWidth: 760,
            mx: "auto",
            lineHeight: 1.7,
          }}
        >
          {isDE
            ? "In diesem Lab verstehst du deine Rolle in einer Krise, testest praktische Vorbereitung und siehst Schritt für Schritt, wie sich deine Preparedness verbessert."
            : "In this lab, you will understand your role in a crisis, test practical readiness stations, and see how your preparedness improves step by step."}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 1.5,
          }}
        >
          {cards.map(([title, text]) => (
            <Box
              key={title}
              sx={{
                minHeight: 142,
                p: 2.25,
                borderRadius: 2,
                textAlign: "left",
                backgroundColor: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.16)",
                backdropFilter: "blur(8px)",
              }}
            >
              <Typography sx={{ color: "#fff", fontWeight: 950, mb: 1 }}>
                {title}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.76)", lineHeight: 1.55 }}>
                {text}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box>
          <Button
            variant="contained"
            onClick={() => handlePageChange("Entry")}
            sx={{
              borderRadius: 999,
              px: 4,
              py: 1.35,
              textTransform: "none",
              fontWeight: 900,
              background: "linear-gradient(90deg, #b50d20, #d6293e)",
              "&:hover": {
                background: "linear-gradient(90deg, #9f0b1c, #c32136)",
              },
            }}
          >
            {isDE ? "Tippen zum Starten" : "Tap to enter"}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
